import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Lane CRUD page in a tab.
 */
const LANE_CRUD_ROUTE = "/home/lanes/laneConfigCrud";

//endregion

//region Component

/**
 * Renders the Lanes configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the Lanes MTable;
 * - use the Locations permission module;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - persist the selected Lane row before opening View/Edit tabs.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Lanes configuration page.
 */
export default function LaneConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Removes table actions that are not required in this configuration page.
     *
     * Important:
     * - Print is not part of the desired Lanes workflow;
     * - Copy is disabled to keep lane creation explicit.
     */
    const removeContextItems = useMemo(() => ["Print", "Copy"], []);

    /**
     * Opens the Lane CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected lane row when required;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected lane row.
     * @returns {void}
     */
    const openLaneCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "LaneLine",
                        value: row,
                    })
                );
            }

            const laneLabel = `${row?.Source ?? ""} → ${row?.Destination ?? ""}`;

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewLane")
                    : operation === "View"
                        ? `${t("LabelViewLane")} "${laneLabel}"`
                        : `${t("LabelEditLane")} "${laneLabel}"`;

            onCreateTab(LANE_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Lane workflow.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openLaneCrud("View", row)],
                ["Create", () => openLaneCrud("Create")],
                ["Edit", (row) => openLaneCrud("Edit", row)],
            ]),
        [openLaneCrud]
    );

    return (
        <div
            className="config-page"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header">
                <div className="config-page__title">
                    {t("Lanes")}
                </div>

                <div className="config-page__description">
                    {t("InfoLanes")}
                </div>
            </div>

            <div className="config-page__table">
                <MTable
                    filtersOnTable
                    entityName="Lanes"
                    roleName="Locations"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

LaneConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
};

//endregion